import React, { useState, useEffect, useMemo } from "react";
import { T, AWARD_COLORS, fmt } from "./theme";
import { SectionHeader, Card, Avatar, Btn, Input, Badge, Sel, PilulaAwardTime } from "./ui";
import { Icon } from "./Icons";
import { supabase, isSupabaseReady } from "./supabase";
import { calcAwardVitalicio } from "./awards";
import { AWARDS } from "./data";
import { criarPedidoWix, criarVendaPendente, upsertComissaoVenda, ModalFechamento, ModalProjeto } from "./PlantaoModais";

// ═══════════════════════════════════════════════════════════════════════
// MÓDULO DE VENDAS
//
// Lista pedidos do Wix (webhook), cadastro manual, vínculo de corretor.
// ═══════════════════════════════════════════════════════════════════════

const STATUS_CORES = {
  pendente:   { bg: "#d7be7c22", border: "#d7be7c55", fg: "#d7be7c", label: "Pendente" },
  confirmada: { bg: "#4ade8022", border: "#4ade8055", fg: "#4ade80", label: "Confirmada" },
  paga:       { bg: "#22d3ee22", border: "#22d3ee55", fg: "#22d3ee", label: "Paga" },
  cancelada:  { bg: "#f4726022", border: "#f4726055", fg: "#f47260", label: "Cancelada" },
  estornada:  { bg: "#a0a0a022", border: "#a0a0a055", fg: "#a0a0a0", label: "Estornada" },
};

export default function Vendas({ corretores, times, currentUser }) {
  const [vendas, setVendas] = useState([]);
  const [ciclosPagos, setCiclosPagos] = useState({}); // "corretor_id|YYYY-MM" → true (ciclo pago = trava edição)
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState("lista"); // lista | nova
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [buscaVenda, setBuscaVenda] = useState("");
  const [filtroPeriodo, setFiltroPeriodo] = useState("todos");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");

  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "admin_master" || currentUser?.role === "proprietario" || currentUser?.role === "comercial";

  // Backfill Wix
  const [syncFrom, setSyncFrom] = useState("2026-03-01");
  const [syncTo, setSyncTo] = useState(new Date().toISOString().slice(0, 10));
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Sync de contatos do Wix → clientes
  const [contatosRunning, setContatosRunning] = useState(false);
  const [contatosResult, setContatosResult] = useState(null);
  async function sincronizarContatos() {
    setContatosRunning(true); setContatosResult(null);
    try {
      const res = await fetch("/api/wix-contacts-sync", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      setContatosResult(await res.json());
    } catch (e) {
      setContatosResult({ ok: false, erro: String(e.message || e) });
    } finally { setContatosRunning(false); }
  }

  function aplicarPreset(preset) {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = hoje.getMonth(); // 0..11
    const fmtData = d => d.toISOString().slice(0, 10);
    if (preset === "hoje") {
      const h = fmtData(hoje);
      setSyncFrom(h); setSyncTo(h);
    } else if (preset === "mes_atual") {
      setSyncFrom(fmtData(new Date(ano, mes, 1)));
      setSyncTo(fmtData(hoje));
    } else if (preset === "mes_passado") {
      setSyncFrom(fmtData(new Date(ano, mes - 1, 1)));
      setSyncTo(fmtData(new Date(ano, mes, 0))); // último dia do mês anterior
    } else if (preset === "3m") {
      setSyncFrom(fmtData(new Date(ano, mes - 3, 1)));
      setSyncTo(fmtData(hoje));
    } else if (preset === "6m") {
      setSyncFrom(fmtData(new Date(ano, mes - 6, 1)));
      setSyncTo(fmtData(hoje));
    } else if (preset === "ano") {
      setSyncFrom(`${ano}-01-01`);
      setSyncTo(fmtData(hoje));
    } else if (preset === "tudo") {
      setSyncFrom("2026-03-01");
      setSyncTo(fmtData(hoje));
    }
  }

  async function sincronizarWix() {
    setSyncRunning(true);
    setSyncResult(null);
    try {
      const res = await fetch("/api/wix-backfill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dateFrom: syncFrom, dateTo: syncTo }),
      });
      const data = await res.json();
      setSyncResult(data);
      if (data.ok && data.totalNovos > 0) {
        // recarrega lista
        const { data: refresh } = await supabase.from("vendas").select("*").order("data_pedido", { ascending: false }).limit(200);
        setVendas(refresh || []);
      }
    } catch (e) {
      setSyncResult({ ok: false, error: e.message });
    }
    setSyncRunning(false);
  }

  // ─── Buscar vendas do Supabase ──────────────────────────────────────
  useEffect(() => {
    async function load() {
      if (!isSupabaseReady) { setLoading(false); return; }
      try {
        const { data, error } = await supabase
          .from("vendas")
          .select("*")
          .order("data_pedido", { ascending: false })
          .limit(200);
        if (error) throw error;
        setVendas(data || []);
        // Ciclos PAGOS travam a edição do financeiro/comissão das vendas daquele corretor/mês
        try {
          const { data: cic } = await supabase.from("ciclos_pagamento").select("corretor_id, mes_ref, status").eq("status", "pago").limit(10000);
          const map = {};
          (cic || []).forEach(c => { if (c.corretor_id && c.mes_ref) map[`${c.corretor_id}|${c.mes_ref}`] = true; });
          setCiclosPagos(map);
        } catch (_) {}
      } catch (err) {
        console.error("[Vendas] Erro ao buscar:", err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Ciclo/mês da comissão de uma venda (mesmo critério do upsert)
  function mesRefVenda(v) {
    return (v.financeiro && v.financeiro.ciclo_mes) || (v.data_pedido ? String(v.data_pedido).slice(0, 7) : "");
  }
  function vendaTravada(v) {
    if (!v.corretor_id) return false;
    return !!ciclosPagos[`${v.corretor_id}|${mesRefVenda(v)}`];
  }

  async function recarregarVendas() {
    if (!isSupabaseReady) return;
    const { data } = await supabase.from("vendas").select("*").order("data_pedido", { ascending: false }).limit(200);
    setVendas(data || []);
  }

  // +Nova Venda (gestor) → mesmo formulário de fechamento do plantão.
  // Venda entra PENDENTE (sem Wix); o pedido Wix é gerado ao confirmar (normal) ou quitar.
  async function salvarNovaVenda(_leadId, ficha) {
    try {
      const lead = {
        id: null,
        nome_lead: [ficha.pedido_info?.nome, ficha.pedido_info?.sobrenome].filter(Boolean).join(" "),
        telefone_lead: ficha.pedido_info?.whatsapp || ficha.pedido_info?.telefone || "",
        campanha: "manual",
      };
      await criarVendaPendente(lead, ficha, ficha.corretor_id || null);
      setVista("lista");
      await recarregarVendas();
    } catch (e) { alert("Erro ao salvar venda: " + e.message); }
  }

  // ─── Vincular corretor a venda + calcular comissão do award ────────
  async function vincularCorretor(vendaId, corretorUuid) {
    if (!isSupabaseReady) return;
    try {
      const venda = vendas.find(v => v.id === vendaId);
      const cor = corretorUuid ? corretores.find(c => c._uuid === corretorUuid) : null;
      let payload = { corretor_id: corretorUuid || null };
      if (cor && venda) {
        const bruto = parseFloat(venda.valor) || 0;
        const frete = parseFloat(venda.financeiro?.frete_cotacao) || parseFloat(venda.frete) || 0;
        const taxa  = parseFloat(venda.taxa_cartao) || 0;
        const liquido = Math.max(0, bruto - frete - taxa);
        const awardId = calcAwardVitalicio(cor).id;
        const award = AWARDS.find(a => a.id === awardId);
        const pct = award?.pctVendaDireta ?? 0;
        const valorComissao = +(liquido * pct / 100).toFixed(2);
        payload.comissao_pct = pct || null;
        payload.comissao_valor = valorComissao || null;
      } else {
        // Desvincula → limpa comissão
        payload.comissao_pct = null;
        payload.comissao_valor = null;
      }
      const { error } = await supabase
        .from("vendas")
        .update(payload)
        .eq("id", vendaId);
      if (error) throw error;

      // A comissão precisa SEGUIR o corretor: se a venda já está confirmada/paga,
      // reatribui a linha de comissão ao novo corretor (ou apaga se desvinculou).
      const jaConfirmada = venda && (venda.status === "confirmada" || venda.status === "paga");
      if (jaConfirmada) {
        if (corretorUuid && cor) {
          await upsertComissaoVenda({ ...venda, ...payload, id: vendaId }, cor);
        } else {
          // Desvinculou → tira a comissão do total do mês
          await supabase.from("comissoes").delete().eq("venda_id", vendaId);
        }
      }

      setVendas(prev => prev.map(v =>
        v.id === vendaId ? { ...v, ...payload } : v
      ));
    } catch (err) {
      alert("Erro ao vincular: " + err.message);
    }
  }

  // ─── Alterar status ──────────────────────────────────────────────────
  // Confirmar é o GATILHO: venda normal gera pedido Wix (número) na confirmação;
  // consórcio/aluguel esperam a quitação. Em ambos, gera/atualiza a comissão pelo award.
  async function alterarStatus(vendaId, novoStatus) {
    if (!isSupabaseReady) return;
    try {
      const venda = vendas.find(v => v.id === vendaId);
      const confirmando = novoStatus === "confirmada" && venda && venda.status !== "confirmada";
      const tipo = venda?.tipo_venda || "normal";
      const diferida = tipo === "consorcio" || tipo === "aluguel"; // Wix só na quitação
      const cor = venda?.corretor_id ? corretores.find(c => c._uuid === venda.corretor_id) : null;

      // Venda à vista/cartão/pix/flex, confirmando e sem nº Wix → gera o pedido Wix agora
      if (confirmando && !diferida && !venda.wix_order_id) {
        const ficha = {
          moveis: venda.moveis || [],
          pedido_info: venda.pedido_info || { nome: venda.cliente_nome, telefone: venda.cliente_telefone },
          financeiro: venda.financeiro || { valor_total: venda.valor },
        };
        const lead = { id: venda.id, nome_lead: venda.cliente_nome, telefone_lead: venda.cliente_telefone, campanha: "manual" };
        const w = await criarPedidoWix(lead, ficha, venda.corretor_id, venda.id, { modo: "confirmar" });
        if (!(w && w.ok && w.numero)) {
          alert("Não foi possível gerar o pedido Wix: " + ((w && (w.erro || JSON.stringify(w.resposta))) || "erro"));
          return;
        }
        // Comissão automática + atualiza estado local
        await upsertComissaoVenda({ ...venda, status: "confirmada", wix_order_id: String(w.numero) }, cor);
        setVendas(prev => prev.map(v => v.id === vendaId ? { ...v, status: "confirmada", wix_order_id: String(w.numero) } : v));
        return;
      }

      // Consórcio/aluguel confirmando (ou normal sem Wix): só grava status + gera comissão agendada
      const { error } = await supabase.from("vendas").update({ status: novoStatus }).eq("id", vendaId);
      if (error) throw error;
      if (confirmando && venda?.corretor_id) {
        await upsertComissaoVenda({ ...venda, status: novoStatus }, cor);
      }
      // Despromover/cancelar (sai de confirmada/paga) → tira a comissão do total do mês
      const desconfirmando = novoStatus !== "confirmada" && novoStatus !== "paga"
        && venda && (venda.status === "confirmada" || venda.status === "paga");
      if (desconfirmando) {
        await supabase.from("comissoes").delete().eq("venda_id", vendaId);
      }
      setVendas(prev => prev.map(v => v.id === vendaId ? { ...v, status: novoStatus } : v));
    } catch (err) {
      alert("Erro ao alterar status: " + err.message);
    }
  }

  // ─── Excluir venda (admin) ────────────────────────────────────────────
  async function excluirVenda(venda) {
    if (!isSupabaseReady) return;
    if (!isAdmin) return;
    const valor = parseFloat(venda.valor || 0);
    if (!window.confirm(`Excluir venda de ${venda.cliente_nome || "?"} (${fmt.brl(valor)})?\nComissões geradas serão removidas em cascata.`)) return;
    try {
      // Apaga comissões da venda antes (FK)
      await supabase.from("comissoes").delete().eq("venda_id", venda.id);
      const { error } = await supabase.from("vendas").delete().eq("id", venda.id);
      if (error) throw error;
      setVendas(prev => prev.filter(v => v.id !== venda.id));
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    }
  }

  // Quitação (consórcio/aluguel): gera pedido Wix (número) + libera comissão. Feito na própria venda.
  async function marcarQuitadoConsorcio(v) {
    if (!isSupabaseReady) return;
    const rot = v.tipo_venda === "aluguel" ? "aluguel" : "consórcio";
    if (!window.confirm(`Confirmar quitação do ${rot}? Vai gerar o pedido no Wix (número), liberar a comissão a receber e mandar pra fabricação.`)) return;
    try {
      // Ficha: prefere a guardada na própria venda; senão busca no plantao_lead vinculado
      let ficha = (v.moveis || v.pedido_info || v.financeiro)
        ? { moveis: v.moveis || [], pedido_info: v.pedido_info || { nome: v.cliente_nome, telefone: v.cliente_telefone }, financeiro: v.financeiro || { valor_total: v.valor } }
        : null;
      const { data: leadsLink } = await supabase
        .from("plantao_leads")
        .select("id, moveis, pedido_info, financeiro, nome_lead, telefone_lead")
        .eq("venda_id", v.id).limit(1);
      const leadLink = leadsLink && leadsLink[0];
      if (!ficha) {
        ficha = leadLink
          ? { moveis: leadLink.moveis, pedido_info: leadLink.pedido_info, financeiro: leadLink.financeiro }
          : { moveis: [], pedido_info: { nome: v.cliente_nome, telefone: v.cliente_telefone }, financeiro: { valor_total: v.valor } };
      }
      const lead = leadLink || { id: v.id, nome_lead: v.cliente_nome, telefone_lead: v.cliente_telefone };

      const w = await criarPedidoWix(lead, ficha, v.corretor_id, v.id, { modo: "quitar" });
      if (w && w.ok && w.numero) {
        if (leadLink) {
          await supabase.from("plantao_leads").update({ wix_numero: String(w.numero), consorcio_quitado: true }).eq("id", leadLink.id);
        }
        setVendas(prev => prev.map(x => x.id === v.id ? { ...x, comissao_quitada: true, status: "confirmada", wix_order_id: String(w.numero) } : x));
      } else {
        alert("Falha ao gerar pedido Wix: " + (w && (w.erro || JSON.stringify(w.resposta)) || "erro"));
      }
    } catch (e) { alert("Erro: " + e.message); }
  }

  // ─── Filtros ──────────────────────────────────────────────────────────
  // Base por PERÍODO (semana/mês/ano/custom) — os KPIs de cima seguem esse recorte.
  const vendasPeriodo = useMemo(() => {
    if (filtroPeriodo === "todos") return vendas;
    const agora = new Date();
    let inicio = null, fim = null;
    if (filtroPeriodo === "dia") {
      inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
    } else if (filtroPeriodo === "semana") {
      inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - agora.getDay());
    } else if (filtroPeriodo === "mes") {
      inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
    } else if (filtroPeriodo === "ano") {
      inicio = new Date(agora.getFullYear(), 0, 1);
    } else if (filtroPeriodo === "custom") {
      if (dataDe) inicio = new Date(dataDe + "T00:00:00");
      if (dataAte) fim = new Date(dataAte + "T23:59:59");
    }
    return vendas.filter(v => {
      if (!v.data_pedido) return false;
      const dt = new Date(v.data_pedido);
      if (inicio && dt < inicio) return false;
      if (fim && dt > fim) return false;
      return true;
    });
  }, [vendas, filtroPeriodo, dataDe, dataAte]);

  // Lista exibida = período + status + busca
  const vendasFiltradas = useMemo(() => {
    let r = vendasPeriodo;
    if (filtroStatus === "sem_corretor") r = r.filter(v => !v.corretor_id);
    else if (filtroStatus !== "todos") r = r.filter(v => v.status === filtroStatus);
    if (buscaVenda) {
      const q = buscaVenda.toLowerCase();
      r = r.filter(v =>
        (v.cliente_nome || "").toLowerCase().includes(q) ||
        (v.wix_order_id || "").toLowerCase().includes(q)
      );
    }
    return r;
  }, [vendasPeriodo, filtroStatus, buscaVenda]);

  // ─── KPIs (seguem o período selecionado) ───────────────────────────────
  const totalVendas = vendasPeriodo.length;
  const totalPendentes = vendasPeriodo.filter(v => v.status === "pendente").length;
  const totalConfirmadas = vendasPeriodo.filter(v => v.status === "confirmada" || v.status === "paga").length;
  const semVinculo = vendasPeriodo.filter(v => !v.corretor_id).length;
  const volumeTotal = vendasPeriodo.filter(v => v.status !== "cancelada" && v.status !== "estornada")
    .reduce((s, v) => s + (parseFloat(v.valor) || 0), 0);

  // Quebra do volume por tipo/forma de pagamento (consórcio, aluguel, cartão, à vista)
  const volBreak = useMemo(() => {
    const acc = { consorcio: 0, aluguel: 0, cartao: 0, avista: 0, flex: 0 };
    vendasPeriodo.filter(v => v.status !== "cancelada" && v.status !== "estornada").forEach(v => {
      const val = parseFloat(v.valor) || 0;
      const tv = v.tipo_venda;
      const fp = v.financeiro?.forma_pagamento;
      if (tv === "consorcio") acc.consorcio += val;
      else if (tv === "aluguel") acc.aluguel += val;
      else if (fp === "cartao") acc.cartao += val;
      else if (fp === "pix") acc.avista += val;
      else if (fp === "flexivel") acc.flex += val;
    });
    return acc;
  }, [vendasPeriodo]);

  // ─── Mapa UUID → corretor ──────────────────────────────────────────
  const corretorPorUuid = useMemo(() => {
    const m = {};
    corretores.forEach(c => { if (c._uuid) m[c._uuid] = c; });
    return m;
  }, [corretores]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>
        Carregando vendas...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader
        icon="🛒"
        title="Vendas"
        subtitle={`${totalVendas} pedidos · ${fmt.brl(volumeTotal)} em volume`}
      />

      {/* ═══ KPIs ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        <KpiMini icon="📦" label="Total" valor={totalVendas} cor={T.brand}
          onClick={() => setFiltroStatus("todos")} ativo={filtroStatus === "todos"} />
        <KpiMini icon="⏳" label="Pendentes" valor={totalPendentes} cor={T.gold}
          onClick={() => setFiltroStatus("pendente")} ativo={filtroStatus === "pendente"} />
        <KpiMini icon="✅" label="Confirmadas" valor={totalConfirmadas} cor={T.ok}
          onClick={() => setFiltroStatus("confirmada")} ativo={filtroStatus === "confirmada"} />
        <KpiMini icon="⚠️" label="Sem corretor" valor={semVinculo} cor={T.err}
          onClick={() => setFiltroStatus("sem_corretor")} ativo={filtroStatus === "sem_corretor"} />
        <KpiMini icon="💰" label="Volume" valor={fmt.brl(volumeTotal)} cor={T.info} isText
          sub={[
            { label: "Cartão", v: volBreak.cartao },
            { label: "À vista", v: volBreak.avista },
            { label: "Consórcio", v: volBreak.consorcio },
            { label: "Aluguel", v: volBreak.aluguel },
            { label: "Flex", v: volBreak.flex },
          ].filter(x => x.v > 0).map(x => ({ label: x.label, valor: fmt.brl(x.v) }))} />
      </div>

      {/* ═══ BACKFILL WIX (admin) — escondido por padrão atrás de details ═══ */}
      {isAdmin && (
        <details>
          <summary style={{
            fontSize: 11, color: T.textDim, cursor: "pointer",
            padding: "6px 0", letterSpacing: 0.5, userSelect: "none",
          }}>🔧 Avançado: sincronizar pedidos do Wix</summary>
        <Card style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                🔄 Sincronizar pedidos do Wix
              </div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
                Importa pedidos do período selecionado. Duplicados são ignorados.
              </div>
            </div>
          </div>

          {/* Presets */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
            {[
              { id: "hoje",         label: "Hoje" },
              { id: "mes_atual",    label: "Mês atual" },
              { id: "mes_passado",  label: "Mês passado" },
              { id: "3m",           label: "Últimos 3 meses" },
              { id: "6m",           label: "Últimos 6 meses" },
              { id: "ano",          label: "Ano atual" },
              { id: "tudo",         label: "Desde mar/2026" },
            ].map(p => (
              <button key={p.id} onClick={() => aplicarPreset(p.id)} disabled={syncRunning} style={{
                padding: "6px 10px", borderRadius: T.r.md,
                background: T.card, border: `1px solid ${T.borderMid}`,
                color: T.textSec, cursor: "pointer", fontSize: 11, fontFamily: "inherit",
              }}>{p.label}</button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 9, color: T.textDim, marginBottom: 4 }}>De</div>
              <input type="date" value={syncFrom} onChange={e => setSyncFrom(e.target.value)} disabled={syncRunning}
                style={{ padding: "8px 10px", background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: T.r.md, fontFamily: "inherit", fontSize: 12 }} />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.textDim, marginBottom: 4 }}>Até</div>
              <input type="date" value={syncTo} onChange={e => setSyncTo(e.target.value)} disabled={syncRunning}
                style={{ padding: "8px 10px", background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: T.r.md, fontFamily: "inherit", fontSize: 12 }} />
            </div>
            <Btn onClick={sincronizarWix} disabled={syncRunning}>{syncRunning ? "Sincronizando..." : "🔄 Sincronizar"}</Btn>
          </div>

          {syncResult && (
            <div style={{
              marginTop: 12, padding: "10px 12px", borderRadius: T.r.md,
              background: syncResult.ok ? T.ok + "15" : T.err + "15",
              border: `1px solid ${syncResult.ok ? T.ok : T.err}44`,
              color: syncResult.ok ? T.ok : T.err, fontSize: 12,
            }}>
              {syncResult.ok ? (
                <>
                  ✅ {syncResult.totalNovos} novos · {syncResult.totalDuplicados} já existentes · {syncResult.totalErros} erros
                  {" · "}{syncResult.paginas} página(s)
                  {syncResult.primeiraData && (
                    <div style={{ marginTop: 4, fontSize: 10, opacity: 0.8 }}>
                      Cobertura: {new Date(syncResult.primeiraData).toLocaleDateString("pt-BR")} → {new Date(syncResult.ultimaData).toLocaleDateString("pt-BR")}
                    </div>
                  )}
                </>
              ) : (
                <>⚠️ {syncResult.error || "Erro desconhecido"}</>
              )}
            </div>
          )}

          {/* Sincronizar CONTATOS do Wix → clientes */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px dashed ${T.border}` }}>
            <div style={{ fontSize: 12, color: T.textSec, marginBottom: 8 }}>
              👥 Importar a lista de <b>contatos do Wix</b> pra base de clientes (pra selecionar em vendas recorrentes).
            </div>
            <Btn onClick={sincronizarContatos} disabled={contatosRunning}>
              {contatosRunning ? "Sincronizando contatos..." : "🔄 Sincronizar contatos do Wix"}
            </Btn>
            {contatosResult && (
              <div style={{
                marginTop: 10, padding: "10px 12px", borderRadius: T.r.md,
                background: contatosResult.ok ? T.ok + "15" : T.err + "15",
                border: `1px solid ${contatosResult.ok ? T.ok : T.err}44`,
                color: contatosResult.ok ? T.ok : T.err, fontSize: 12,
              }}>
                {contatosResult.ok
                  ? <>✅ {contatosResult.novos} novos · {contatosResult.ja_existiam} já existentes{contatosResult.removidos ? ` · ${contatosResult.removidos} removidos (apagados no Wix)` : ""} · {contatosResult.total_wix} contatos no Wix</>
                  : <>⚠️ {contatosResult.etapa === "wix" ? `Wix ${contatosResult.status}: ` : ""}{contatosResult.erro || (contatosResult.resposta && JSON.stringify(contatosResult.resposta)) || "Erro"}</>}
              </div>
            )}
          </div>
        </Card>
        </details>
      )}

      {/* ═══ LISTA DE VENDAS ═══ */}
      {vista === "lista" && (
        <>
          {/* Filtros + Nova venda */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Input
              placeholder="Buscar por cliente ou pedido..."
              value={buscaVenda}
              onChange={e => setBuscaVenda(e.target.value)}
              style={{ flex: 1, minWidth: 180 }}
            />
            <Sel value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ minWidth: 140 }}>
              <option value="todos">Todos os status</option>
              <option value="sem_corretor">⚠️ Sem corretor</option>
              <option value="pendente">⏳ Pendente</option>
              <option value="confirmada">✅ Confirmada</option>
              <option value="paga">💰 Paga</option>
              <option value="cancelada">❌ Cancelada</option>
              <option value="estornada">↩️ Estornada</option>
            </Sel>
            <Sel value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)} style={{ minWidth: 140 }}>
              <option value="todos">📅 Todo o período</option>
              <option value="dia">📆 Hoje</option>
              <option value="semana">📆 Esta semana (dom→sáb)</option>
              <option value="mes">📆 Este mês</option>
              <option value="ano">📆 Este ano</option>
              <option value="custom">🎯 Personalizado</option>
            </Sel>
            <Btn onClick={() => setVista("nova")} icon={<Icon.plus size={14} />}>
              Nova Venda
            </Btn>
          </div>
          {filtroPeriodo === "custom" && (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
              <label style={{ fontSize: 11, color: T.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                De:
                <input type="date" value={dataDe} onChange={e => setDataDe(e.target.value)}
                  style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "6px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
              </label>
              <label style={{ fontSize: 11, color: T.textSub, display: "flex", alignItems: "center", gap: 4 }}>
                Até:
                <input type="date" value={dataAte} onChange={e => setDataAte(e.target.value)}
                  style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.sm, padding: "6px 8px", color: T.text, fontFamily: "inherit", fontSize: 12 }} />
              </label>
              {(dataDe || dataAte) && (
                <button onClick={() => { setDataDe(""); setDataAte(""); }}
                  style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>
                  🗑️ Limpar
                </button>
              )}
            </div>
          )}

          {/* Lista */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {vendasFiltradas.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: T.textDim, fontSize: 13 }}>
                {vendas.length === 0
                  ? "Nenhuma venda registrada ainda. Vendas chegam automaticamente via webhook do Wix ou podem ser adicionadas manualmente."
                  : "Nenhuma venda encontrada com esses filtros."
                }
              </div>
            ) : (
              vendasFiltradas.map(v => (
                <VendaCard
                  key={v.id}
                  venda={v}
                  corretores={corretores}
                  corretorPorUuid={corretorPorUuid}
                  vincularCorretor={vincularCorretor}
                  alterarStatus={alterarStatus}
                  isAdmin={isAdmin}
                  onExcluir={excluirVenda}
                  onQuitar={marcarQuitadoConsorcio}
                  times={times}
                  travada={vendaTravada(v)}
                />
              ))
            )}
          </div>
        </>
      )}

      {/* ═══ NOVA VENDA MANUAL ═══ */}
      {vista === "nova" && (
        <ModalFechamento
          lead={{ id: null, nome_lead: "", telefone_lead: "", campanha: "manual", pedido_info: {}, moveis: [], financeiro: {} }}
          onSalvar={salvarNovaVenda}
          onCancelar={() => setVista("lista")}
          corretores={corretores}
          isAdmin={isAdmin}
          currentUser={currentUser}
          modoTitulo="Nova venda"
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CARD DE VENDA
// ═══════════════════════════════════════════════════════════════════════

function VendaCard({ venda, corretores, corretorPorUuid, vincularCorretor, alterarStatus, isAdmin, onExcluir, onQuitar, times, travada }) {
  const props = { times };
  const [editando, setEditando] = useState(false);
  const [editandoDetalhes, setEditandoDetalhes] = useState(false);
  const [editComissao, setEditComissao] = useState(false); // comissão automática; edita só se clicar
  const [verFabricacao, setVerFabricacao] = useState(false); // detalhes da fabricação (ficha do fechamento)
  // Normaliza comprovantes (array). Aceita fallback do campo singular antigo.
  const comprovantesIniciais = Array.isArray(venda.taxa_cartao_comprovantes)
    ? venda.taxa_cartao_comprovantes
    : (venda.taxa_cartao_comprovante ? [venda.taxa_cartao_comprovante] : []);
  const [detalhes, setDetalhes] = useState({
    // Prefill com o que o corretor botou no fechamento (venda.financeiro), como fallback
    frete: venda.frete || venda.financeiro?.frete_cliente || 0,
    transportadora: venda.frete_transportadora || venda.transportadora || venda.financeiro?.transportadora || "",
    taxa_cartao: venda.taxa_cartao || 0,
    taxa_cartao_comprovantes: comprovantesIniciais,
    comissao_pct: venda.comissao_pct ?? venda.percentual ?? "",
    comissao_valor: venda.comissao_valor ?? venda.valor ?? "",
    tipo_venda: venda.tipo_venda || "normal",
    comissao_data_prevista: venda.comissao_data_prevista || "",
    // Cotação de frete que o corretor lançou (pro admin confirmar)
    frete_cotacao: venda.financeiro?.frete_cotacao ?? "",
    cotacao_numero: venda.financeiro?.cotacao_numero ?? "",
    // Ciclo/mês em que a comissão dessa venda é considerada (mes_ref)
    ciclo_mes: venda.financeiro?.ciclo_mes || (venda.data_pedido ? String(venda.data_pedido).slice(0, 7) : ""),
  });
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const v = venda;
  const st = STATUS_CORES[v.status] || STATUS_CORES.pendente;
  const corretor = v.corretor_id ? corretorPorUuid[v.corretor_id] : null;
  const isWix = !!v.wix_order_id;
  const data = v.data_pedido ? new Date(v.data_pedido).toLocaleDateString("pt-BR") : "—";
  const hora = v.data_pedido ? new Date(v.data_pedido).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
  const valorBruto = parseFloat(v.valor) || 0;
  // Frete comissionável = cotação de frete (do fechamento); fallback no frete antigo
  const cotacaoFrete = parseFloat(v.financeiro?.frete_cotacao) || parseFloat(v.frete) || 0;
  const taxaCartao = parseFloat(v.taxa_cartao) || 0;
  const valorLiquido = Math.max(0, valorBruto - cotacaoFrete - taxaCartao);

  async function salvarDetalhes() {
    if (!isSupabaseReady) return;
    try {
      const novoLiquido = Math.max(0, valorBruto - (parseFloat(detalhes.frete_cotacao) || 0) - (parseFloat(detalhes.taxa_cartao) || 0));
      const novoPct = detalhes.comissao_pct === "" ? null : parseFloat(detalhes.comissao_pct);
      const novoValor = detalhes.comissao_valor === "" ? null : parseFloat(detalhes.comissao_valor);
      const tipoVenda = detalhes.tipo_venda || "normal";
      // Data prevista só faz sentido em consórcio; em venda normal fica null
      const dataPrevista = tipoVenda === "consorcio" ? (detalhes.comissao_data_prevista || null) : null;
      const cicloMes = detalhes.ciclo_mes || null;
      const novoFinanceiro = {
        ...(v.financeiro || {}),
        ciclo_mes: cicloMes,
        transportadora: detalhes.transportadora || null,
        frete_cotacao: detalhes.frete_cotacao === "" ? null : parseFloat(detalhes.frete_cotacao),
        cotacao_numero: detalhes.cotacao_numero || null,
      };
      const { error } = await supabase
        .from("vendas")
        .update({
          frete: 0,
          frete_transportadora: detalhes.transportadora || null,
          taxa_cartao: parseFloat(detalhes.taxa_cartao) || 0,
          taxa_cartao_comprovantes: detalhes.taxa_cartao_comprovantes || [],
          valor_liquido: novoLiquido,
          comissao_pct: novoPct,
          comissao_valor: novoValor,
          tipo_venda: tipoVenda,
          comissao_data_prevista: dataPrevista,
          financeiro: novoFinanceiro,
        })
        .eq("id", v.id);
      if (error) throw error;
      // Move a comissão pro ciclo/mês escolhido (mes_ref) → recalcula o ciclo automaticamente
      if (cicloMes) {
        try { await supabase.from("comissoes").update({ mes_ref: cicloMes }).eq("venda_id", v.id); } catch (_) {}
      }
      Object.assign(v, {
        frete: 0,
        frete_transportadora: detalhes.transportadora,
        taxa_cartao: parseFloat(detalhes.taxa_cartao) || 0,
        taxa_cartao_comprovantes: detalhes.taxa_cartao_comprovantes || [],
        valor_liquido: novoLiquido,
        comissao_pct: novoPct,
        comissao_valor: novoValor,
        tipo_venda: tipoVenda,
        comissao_data_prevista: dataPrevista,
        financeiro: novoFinanceiro,
      });
      setEditandoDetalhes(false);
    } catch (err) {
      alert("Erro: " + err.message);
    }
  }

  // Award do corretor (usado pra default de %)
  const awardCorretor = corretor ? calcAwardVitalicio(corretor).id : null;
  const awardData = awardCorretor ? AWARDS.find(a => a.id === awardCorretor) : null;
  const pctDefaultAward = awardData?.pctVendaDireta ?? 0;

  // Líquido comissionável ao vivo, conforme cotação de frete + taxa editadas
  const liquidoEdit = Math.max(0, valorBruto - (parseFloat(detalhes.frete_cotacao) || 0) - (parseFloat(detalhes.taxa_cartao) || 0));
  function editarPct(val) {
    const pct = val === "" ? "" : parseFloat(val);
    const valor = pct === "" ? "" : +((liquidoEdit * pct / 100).toFixed(2));
    setDetalhes(d => ({ ...d, comissao_pct: val, comissao_valor: valor === "" ? "" : String(valor) }));
  }

  function editarValor(val) {
    const valor = val === "" ? "" : parseFloat(val);
    const pct = liquidoEdit > 0 && valor !== "" ? +((valor / liquidoEdit) * 100).toFixed(2) : "";
    setDetalhes(d => ({ ...d, comissao_valor: val, comissao_pct: pct === "" ? "" : String(pct) }));
  }
  // Recalcula a comissão automática (award) quando cotação/taxa mudam.
  // Só age se a comissão estiver no % do award (auto). Se o admin pôs % custom, respeita.
  useEffect(() => {
    if (editComissao || !corretor) return;
    const isAuto = detalhes.comissao_pct === "" || detalhes.comissao_pct == null || String(detalhes.comissao_pct) === String(pctDefaultAward);
    if (!isAuto) return;
    const novo = +((liquidoEdit * pctDefaultAward) / 100).toFixed(2);
    setDetalhes(d => {
      const alvoPct = String(pctDefaultAward);
      if (String(d.comissao_pct) === alvoPct && String(d.comissao_valor) === String(novo)) return d;
      return { ...d, comissao_pct: alvoPct, comissao_valor: String(novo) };
    });
  }, [liquidoEdit, pctDefaultAward, editComissao, corretor, detalhes.comissao_pct]);

  async function uploadComprovante(files) {
    if (!files || files.length === 0 || !isSupabaseReady) return;
    setUploadingComprovante(true);
    try {
      const novos = [];
      for (const file of files) {
        const ext = (file.name.split(".").pop() || "bin").toLowerCase();
        const path = `comprovantes/venda-${v.id}-${Date.now()}-${Math.round(Math.random() * 1e6)}.${ext}`;
        const { error } = await supabase.storage.from("nfs").upload(path, file, { upsert: false, contentType: file.type });
        if (error) throw error;
        const { data: pub } = supabase.storage.from("nfs").getPublicUrl(path);
        novos.push(pub?.publicUrl || path);
      }
      setDetalhes(d => ({ ...d, taxa_cartao_comprovantes: [...(d.taxa_cartao_comprovantes || []), ...novos] }));
    } catch (err) {
      alert("Erro ao subir comprovante: " + err.message);
    } finally {
      setUploadingComprovante(false);
    }
  }

  function removerComprovante(idx) {
    setDetalhes(d => ({
      ...d,
      taxa_cartao_comprovantes: (d.taxa_cartao_comprovantes || []).filter((_, i) => i !== idx),
    }));
  }

  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${st.fg}`,
      borderRadius: T.r.md, padding: "14px 16px",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: T.textDim, fontFamily: "monospace" }}>
            {isWix ? `#${v.wix_order_id}` : `✍️ Manual`}
          </span>
          <div style={{
            padding: "2px 8px", borderRadius: 100,
            background: st.bg, border: `1px solid ${st.border}`,
            fontSize: 10, fontWeight: 600, color: st.fg,
          }}>
            {st.label}
          </div>
          {(() => {
            const TIPO_PILL = {
              cartao:    { txt: "💳 Cartão",    cor: T.info },
              pix:       { txt: "⚡ À vista",   cor: T.ok },
              flexivel:  { txt: "🔀 Flex",      cor: T.textSec },
              consorcio: { txt: "📅 Consórcio", cor: T.brand },
              aluguel:   { txt: "🏠 Aluguel",   cor: T.gold },
            };
            const p = TIPO_PILL[v.tipo_venda];
            if (!p) return null;
            return <span style={{ padding: "2px 8px", borderRadius: 100, background: p.cor + "22", color: p.cor, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>{p.txt}</span>;
          })()}
        </div>
        <span style={{ fontSize: 11, color: T.textDim }}>{data} {hora}</span>
      </div>

      {/* Cliente + valores */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 10, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
            {v.cliente_nome || "Cliente não identificado"}
          </div>
          <div className="serif" style={{ fontSize: 22, fontWeight: 700, color: T.gold, marginTop: 4 }}>
            {fmt.brl(valorBruto)}
          </div>
          {/* Detalhes de comissão */}
          {(cotacaoFrete > 0 || taxaCartao > 0 || corretor) && (
            <div style={{ marginTop: 4, fontSize: 10, color: T.textSub }}>
              {cotacaoFrete > 0 && <span>Cotação frete: {fmt.brl(cotacaoFrete)} </span>}
              {v.frete_transportadora && <span>({v.frete_transportadora}) </span>}
              {taxaCartao > 0 && (() => {
                const comps = Array.isArray(v.taxa_cartao_comprovantes)
                  ? v.taxa_cartao_comprovantes
                  : (v.taxa_cartao_comprovante ? [v.taxa_cartao_comprovante] : []);
                return (
                  <span>· Taxa cartão: {fmt.brl(taxaCartao)}
                    {comps.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color: T.brand, marginLeft: 4, textDecoration: "underline" }}>📎{comps.length > 1 ? i + 1 : ""}</a>
                    ))}
                    {" "}
                  </span>
                );
              })()}
              <span style={{ color: T.ok, fontWeight: 600 }}>· Líquido: {fmt.brl(valorLiquido)}</span>
              {corretor && (() => {
                const awardId = calcAwardVitalicio(corretor).id;
                const award = AWARDS.find(a => a.id === awardId);
                const pct = parseFloat(v.comissao_pct ?? award?.pctVendaDireta ?? 0);
                const valor = v.comissao_valor != null ? parseFloat(v.comissao_valor) : +(valorLiquido * pct / 100).toFixed(2);
                return (
                  <span style={{ color: T.gold, fontWeight: 700 }}>
                    {" · "}💰 Comissão: {fmt.brl(valor)} <span style={{ color: T.textSub, fontWeight: 500 }}>({pct.toFixed(2)}%)</span>
                  </span>
                );
              })()}
              {(v.tipo_venda === "consorcio" || v.tipo_venda === "aluguel") && (
                <span style={{ color: T.brand, fontWeight: 600 }}>
                  {" · "}{v.tipo_venda === "aluguel" ? "🏠 Aluguel" : "📅 Consórcio"}
                  {v.comissao_data_prevista && !v.comissao_quitada && ` · comissão prev. ${new Date(v.comissao_data_prevista + "T00:00:00").toLocaleDateString("pt-BR")}`}
                  {v.comissao_quitada && <span style={{ color: T.ok }}> · ✅ quitado</span>}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Corretor vinculado */}
        <div style={{ textAlign: "right" }}>
          {corretor ? (() => {
            const timeDoc = corretor.timeId && (props.times || []).length
              ? props.times.find(t => t.id === corretor.timeId)
              : null;
            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{corretor.nome}</div>
                {corretor.tipo !== "proprietario" && corretor.tipo !== "admin" && corretor.tipo !== "admin_master" && (
                  <PilulaAwardTime awardId={calcAwardVitalicio(corretor).id} timeEmoji={timeDoc?.emoji} size="xs" />
                )}
                {timeDoc && <div style={{ fontSize: 10, color: T.textSub }}>{timeDoc.nome}</div>}
                <div style={{ fontSize: 10, color: T.textSub }}>📍 {corretor.cidade}-{corretor.uf}</div>
              </div>
            );
          })() : (
            <Badge text="SEM CORRETOR" tone="err" />
          )}
        </div>
      </div>

      {/* Tags */}
      {v.tags_match?.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 9, color: T.textDim }}>Tags:</span>
          {v.tags_match.map((t, i) => (
            <span key={i} style={{ padding: "1px 6px", borderRadius: 4, background: T.elev, fontSize: 9, color: T.textSub }}>{t}</span>
          ))}
        </div>
      )}

      {/* Editar detalhes (cotação de frete, transportadora, taxa) */}
      {editandoDetalhes && (
        <div style={{ marginTop: 10, padding: "10px 12px", background: T.cardSoft, borderRadius: T.r.md, border: `1px solid ${T.border}` }}>
          {travada && (
            <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: T.r.md, background: T.warn + "18", border: `1px solid ${T.warn}55`, color: T.warn, fontSize: 11, fontWeight: 600 }}>
              🔒 Pagamento já baixado (ciclo pago). Pra editar, volte esse ciclo pra pendente em Pagamentos.
            </div>
          )}
          {/* Taxa cartão · cotação frete · transportadora · nº cotação (numa linha) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <Input label="Taxa cartão (R$)" type="number" value={detalhes.taxa_cartao} disabled={travada} onChange={e => setDetalhes({ ...detalhes, taxa_cartao: e.target.value })} />
            <Input label="Cotação de frete (R$)" type="number" value={detalhes.frete_cotacao} disabled={travada} onChange={e => setDetalhes({ ...detalhes, frete_cotacao: e.target.value })} placeholder="Valor cotado" />
            <Input label="Transportadora" value={detalhes.transportadora} disabled={travada} onChange={e => setDetalhes({ ...detalhes, transportadora: e.target.value })} placeholder="Nome da transportadora" />
            <Input label="Nº da cotação" value={detalhes.cotacao_numero} disabled={travada} onChange={e => setDetalhes({ ...detalhes, cotacao_numero: e.target.value })} />
          </div>
          <div style={{ marginTop: 10, padding: "10px 12px", background: T.input, borderRadius: T.r.md, border: `1px dashed ${T.border}` }}>
            <div style={{ fontSize: 10, color: T.textSub, marginBottom: 8, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
              📎 Comprovantes da taxa cartão {(detalhes.taxa_cartao_comprovantes?.length || 0) > 0 && `(${detalhes.taxa_cartao_comprovantes.length})`}
            </div>
            {(detalhes.taxa_cartao_comprovantes || []).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                {detalhes.taxa_cartao_comprovantes.map((url, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: T.brand, fontSize: 11, textDecoration: "underline" }}>
                      📎 Comprovante {i + 1}
                    </a>
                    <button
                      onClick={() => removerComprovante(i)}
                      style={{ background: "none", border: "none", color: T.err, cursor: "pointer", fontSize: 10, textDecoration: "underline" }}
                    >🗑️</button>
                  </div>
                ))}
              </div>
            )}
            <label style={{ display: "inline-block", cursor: "pointer" }}>
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                onChange={e => e.target.files?.length && uploadComprovante(Array.from(e.target.files))}
                style={{ display: "none" }}
              />
              <span style={{
                display: "inline-block", padding: "6px 12px", borderRadius: T.r.md,
                background: T.brand, color: T.textInv,
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>
                {uploadingComprovante ? "Enviando…" : "📎 Anexar comprovante(s)"}
              </span>
            </label>
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: T.textSub }}>
            Valor líquido (comissionável): <strong style={{ color: T.ok }}>{fmt.brl(liquidoEdit)}</strong>
            <span style={{ color: T.textDim }}> (bruto − cotação frete − taxa cartão)</span>
          </div>

          {/* Comissão editável */}
          <div style={{ marginTop: 10, padding: "10px 12px", background: T.gold + "10", borderRadius: T.r.md, border: `1px dashed ${T.gold}55` }}>
            <div style={{ fontSize: 10, color: T.gold, marginBottom: 8, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
              💰 Comissão desta venda
            </div>
            {!corretor && (
              <div style={{ fontSize: 11, color: T.warn, marginBottom: 6 }}>⚠️ Vincula um corretor pra calcular a comissão.</div>
            )}
            {corretor && (
              <div style={{ fontSize: 10, color: T.textSub, marginBottom: 8 }}>
                Corretor <b style={{ color: T.text }}>{corretor.nome}</b> · Award <b style={{ color: T.gold }}>{awardCorretor}</b> · % base do award: <b style={{ color: T.gold }}>{pctDefaultAward}%</b>
                {(detalhes.comissao_pct === "" || detalhes.comissao_pct == null) && pctDefaultAward > 0 && (
                  <button
                    onClick={() => editarPct(String(pctDefaultAward))}
                    style={{ background: "none", border: "none", color: T.brand, textDecoration: "underline", cursor: "pointer", fontSize: 10, marginLeft: 6, fontFamily: "inherit" }}
                  >Aplicar {pctDefaultAward}%</button>
                )}
              </div>
            )}
            {corretor && !editComissao ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12, color: T.text }}>
                  Comissão automática: <b style={{ color: T.gold }}>{detalhes.comissao_pct || pctDefaultAward}%</b>
                  {" = "}<b style={{ color: T.gold }}>{fmt.brl(parseFloat(detalhes.comissao_valor) || +(liquidoEdit * (parseFloat(detalhes.comissao_pct || pctDefaultAward) || 0) / 100).toFixed(2))}</b>
                </div>
                {!travada && <Btn variant="ghost" onClick={() => setEditComissao(true)} style={{ fontSize: 11 }}>✏️ Editar comissão</Btn>}
              </div>
            ) : corretor ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <Input label="% Comissão" type="number" value={detalhes.comissao_pct} disabled={travada} onChange={e => editarPct(e.target.value)} placeholder="Ex: 6.5" />
                  <Input label="Valor comissão (R$)" type="number" value={detalhes.comissao_valor} disabled={travada} onChange={e => editarValor(e.target.value)} placeholder="Ex: 350.00" />
                </div>
                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 10, color: T.textDim }}>Editar % recalcula valor e vice-versa. Base: líquido comissionável.</span>
                  <button onClick={() => { editarPct(String(pctDefaultAward)); setEditComissao(false); }} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 10, textDecoration: "underline", fontFamily: "inherit" }}>voltar ao automático</button>
                </div>
              </>
            ) : null}

            {/* Tipo de venda + data prevista da comissão (consórcio) */}
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.gold}33` }}>
              <div style={{ display: "grid", gridTemplateColumns: detalhes.tipo_venda === "consorcio" ? "1fr 1fr" : "1fr", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600 }}>Tipo de venda</div>
                  <Sel
                    value={detalhes.tipo_venda}
                    onChange={e => setDetalhes(d => ({
                      ...d,
                      tipo_venda: e.target.value,
                      comissao_data_prevista: e.target.value === "consorcio" ? d.comissao_data_prevista : "",
                    }))}
                    style={{ width: "100%", fontSize: 12, padding: "6px 8px", background: T.input, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text }}
                  >
                    <option value="cartao">💳 Cartão</option>
                    <option value="pix">⚡ Pix</option>
                    <option value="flexivel">🔀 Flex</option>
                    <option value="consorcio">📅 Consórcio (comissão na quitação)</option>
                    <option value="aluguel">🏠 Aluguel (comissão na quitação)</option>
                    <option value="normal">Normal (antigo)</option>
                  </Sel>
                </div>
                {detalhes.tipo_venda === "consorcio" && (
                  <Input
                    label="Data prevista da comissão"
                    type="date"
                    value={detalhes.comissao_data_prevista || ""}
                    onChange={e => setDetalhes(d => ({ ...d, comissao_data_prevista: e.target.value }))}
                  />
                )}
              </div>
              {detalhes.tipo_venda === "consorcio" && (
                <div style={{ marginTop: 6, fontSize: 10, color: T.textDim }}>
                  A comissão fica <b style={{ color: T.gold }}>agendada</b> e só entra no "a pagar" quando a data chegar ou você marcar a quitação. (data = quitação das parcelas do consórcio)
                </div>
              )}
              {/* Ciclo/mês em que a comissão será considerada (mes_ref) */}
              <div style={{ marginTop: 10 }}>
                <Input
                  label="Ciclo (mês) da comissão"
                  type="month"
                  value={detalhes.ciclo_mes || ""}
                  onChange={e => setDetalhes(d => ({ ...d, ciclo_mes: e.target.value }))}
                />
                <div style={{ marginTop: 4, fontSize: 10, color: T.textDim }}>
                  Define em qual mês essa venda entra no somatório de Pagamentos. Padrão: mês da venda.
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
            {!travada && <Btn variant="ghost" onClick={salvarDetalhes} style={{ fontSize: 11, color: T.ok }}>💾 Salvar</Btn>}
            <Btn variant="ghost" onClick={() => setEditandoDetalhes(false)} style={{ fontSize: 11 }}>{travada ? "Fechar" : "Cancelar"}</Btn>
          </div>
        </div>
      )}

      {/* Ações */}
      <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {!editando ? (
          <>
            <Btn variant="ghost" onClick={() => setEditando(true)} style={{ fontSize: 11 }}>
              ✏️ {corretor ? "Alterar corretor" : "Vincular corretor"}
            </Btn>
            <Btn variant="ghost" onClick={() => setEditandoDetalhes(!editandoDetalhes)} style={{ fontSize: 11 }}>
              💰 Financeiro
            </Btn>
            {((v.moveis && v.moveis.length) || v.pedido_info) && (
              <Btn variant="ghost" onClick={() => setVerFabricacao(true)} style={{ fontSize: 11 }}>
                🏭 Detalhes
              </Btn>
            )}
            {(v.tipo_venda === "consorcio" || v.tipo_venda === "aluguel") && !v.comissao_quitada && onQuitar && (
              <Btn variant="success" onClick={() => onQuitar(v)} style={{ fontSize: 11 }}>💰 Marcar quitado</Btn>
            )}
            {/* Status flexível — pode mudar pra qualquer status */}
            <Sel
              value={v.status}
              onChange={e => alterarStatus(v.id, e.target.value)}
              style={{ fontSize: 11, minWidth: 140, padding: "4px 8px", background: T.cardSoft, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text }}
            >
              <option value="pendente">⏳ Pendente</option>
              <option value="confirmada">✅ Confirmada</option>
              <option value="cancelada">❌ Cancelada</option>
            </Sel>
            {/* Excluir venda (admin only) */}
            {isAdmin && onExcluir && (
              <button
                onClick={() => onExcluir(v)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: T.err, fontSize: 11, fontFamily: "inherit",
                  textDecoration: "underline", padding: "4px 8px",
                }}
              >🗑️ Excluir</button>
            )}
          </>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flex: 1 }}>
            <Sel
              value={v.corretor_id || ""}
              onChange={e => { vincularCorretor(v.id, e.target.value || null); setEditando(false); }}
              style={{ flex: 1, minWidth: 180 }}
            >
              <option value="">— Sem corretor —</option>
              {corretores
                .filter(c => !c.banido)
                .sort((a, b) => {
                  const rank = t => (t === "corretor" || t == null) ? 0 : (t === "admin" ? 1 : 2);
                  return rank(a.tipo) - rank(b.tipo);
                })
                .map(c => {
                  const tag = c.tipo === "proprietario" ? " 🦁" : c.tipo === "admin" ? " 🛡️" : "";
                  return (
                    <option key={c._uuid || c.id} value={c._uuid}>
                      {c.nome}{tag} · {c.cidade}-{c.uf} {c.tagUnica ? `(@${c.tagUnica})` : ""}
                    </option>
                  );
                })}
            </Sel>
            <Btn variant="ghost" onClick={() => setEditando(false)} style={{ fontSize: 11 }}>Cancelar</Btn>
          </div>
        )}
      </div>

      {verFabricacao && (
        <ModalProjeto
          lead={{
            pedido_info: v.pedido_info || { nome: v.cliente_nome, telefone: v.cliente_telefone },
            moveis: v.moveis || [],
            financeiro: v.financeiro || {},
            nome_lead: v.cliente_nome,
            telefone_lead: v.cliente_telefone,
            wix_numero: v.wix_order_id,
          }}
          onClose={() => setVerFabricacao(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// NOVA VENDA MANUAL
// ═══════════════════════════════════════════════════════════════════════

function NovaVenda({ corretores, onSalvar }) {
  const [form, setForm] = useState({
    clienteNome: "", clienteTelefone: "", valorTotal: "", corretorUuid: "", status: "confirmada",
    frete: "", transportadora: "", taxaCartao: "",
  });
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    if (!form.clienteNome.trim()) { setMsg({ tipo: "err", texto: "Nome do cliente é obrigatório." }); return; }
    if (!form.valorTotal || parseFloat(form.valorTotal) <= 0) { setMsg({ tipo: "err", texto: "Valor deve ser maior que zero." }); return; }
    if (!isSupabaseReady) { setMsg({ tipo: "err", texto: "Supabase não conectado." }); return; }

    setSalvando(true);
    try {
      const body = {
        cliente_nome: form.clienteNome.trim(),
        cliente_telefone: form.clienteTelefone.trim() || null,
        valor: parseFloat(form.valorTotal),
        frete: parseFloat(form.frete) || 0,
        frete_transportadora: form.transportadora.trim() || null,
        taxa_cartao: parseFloat(form.taxaCartao) || 0,
        corretor_id: form.corretorUuid || null,
        status: form.status,
        wix_order_id: null,
        data_pedido: new Date().toISOString(),
        tags_match: ["manual"],
      };

      const { data, error } = await supabase
        .from("vendas")
        .insert(body)
        .select()
        .single();

      if (error) throw error;

      setMsg({ tipo: "ok", texto: `✅ Venda de ${fmt.brl(parseFloat(form.valorTotal))} registrada!` });
      onSalvar(data);
      setForm({ clienteNome: "", clienteTelefone: "", valorTotal: "", corretorUuid: "", status: "confirmada", frete: "", transportadora: "", taxaCartao: "" });
    } catch (err) {
      setMsg({ tipo: "err", texto: "Erro ao salvar: " + err.message });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card>
      <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 16 }}>
        ➕ Registrar Venda Manual
      </div>

      {msg && (
        <div style={{
          padding: "10px 14px", borderRadius: T.r.md, marginBottom: 12,
          background: msg.tipo === "ok" ? T.okSoft : T.errSoft,
          border: `1px solid ${msg.tipo === "ok" ? T.ok : T.err}55`,
          color: msg.tipo === "ok" ? T.ok : T.err, fontSize: 12,
        }}>
          {msg.texto}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <Input label="Nome do cliente *" value={form.clienteNome} onChange={e => setForm({ ...form, clienteNome: e.target.value })} placeholder="Ex: Maria Santos" />
        </div>
        <Input label="WhatsApp do cliente" value={form.clienteTelefone} onChange={e => setForm({ ...form, clienteTelefone: e.target.value })} placeholder="(85) 99999-0000" />
        <Input label="Valor total (R$) *" type="number" value={form.valorTotal} onChange={e => setForm({ ...form, valorTotal: e.target.value })} placeholder="15000.00" />
        <Input label="Frete (R$)" type="number" value={form.frete} onChange={e => setForm({ ...form, frete: e.target.value })} placeholder="0.00" />
        <Input label="Transportadora" value={form.transportadora} onChange={e => setForm({ ...form, transportadora: e.target.value })} placeholder="Nome da transportadora" />
        <Input label="Taxa cartão (R$)" type="number" value={form.taxaCartao} onChange={e => setForm({ ...form, taxaCartao: e.target.value })} placeholder="0.00" />
        <Sel label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          <option value="pendente">⏳ Pendente</option>
          <option value="confirmada">✅ Confirmada</option>
          <option value="paga">💰 Paga</option>
        </Sel>
        <div style={{ gridColumn: "1 / -1" }}>
          <Sel label="Corretor(a) responsável" value={form.corretorUuid} onChange={e => setForm({ ...form, corretorUuid: e.target.value })}>
            <option value="">— Selecione —</option>
            {corretores.map(c => (
              <option key={c._uuid || c.id} value={c._uuid}>{c.nome} · {c.cidade}-{c.uf} {c.tagUnica ? `(${c.tagUnica})` : ""}</option>
            ))}
          </Sel>
        </div>
        {form.valorTotal && (
          <div style={{ gridColumn: "1 / -1", padding: "8px 12px", background: T.cardSoft, borderRadius: T.r.md, fontSize: 11, color: T.textSub }}>
            💰 Valor bruto (volume): <strong style={{ color: T.gold }}>{fmt.brl(parseFloat(form.valorTotal) || 0)}</strong>
            &nbsp;·&nbsp;Líquido (comissão): <strong style={{ color: T.ok }}>{fmt.brl(Math.max(0, (parseFloat(form.valorTotal) || 0) - (parseFloat(form.frete) || 0) - (parseFloat(form.taxaCartao) || 0)))}</strong>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <Btn onClick={salvar} disabled={salvando} icon={<span>💾</span>}>
          {salvando ? "Salvando..." : "Registrar venda"}
        </Btn>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════════════

function KpiMini({ icon, label, valor, cor, isText, onClick, ativo, sub }) {
  const clicavel = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      style={{
        background: ativo ? cor + "22" : T.card,
        border: `1px solid ${ativo ? cor : T.border}`,
        borderLeft: `3px solid ${cor}`, borderRadius: T.r.md,
        padding: "12px 10px",
        cursor: clicavel ? "pointer" : "default",
        transition: "background 120ms",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>{label}</span>
      </div>
      <div className="serif" style={{ fontSize: isText ? 15 : 22, fontWeight: 700, color: T.text }}>{valor}</div>
      {sub && sub.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px dashed ${T.border}`, display: "flex", flexDirection: "column", gap: 3 }}>
          {sub.map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 6, fontSize: 10 }}>
              <span style={{ color: T.textSub }}>{s.label}</span>
              <span style={{ color: T.text, fontWeight: 600 }}>{s.valor}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
